import { ALLIED_CAMERAS } from './allied.js';
import { BASLER_CAMERAS, decode as decodeBasler } from './basler.js';
import { COMPUTAR_LENSES } from './computar.js';
import { LUCID_CAMERAS } from './lucid.js';
import { OMRON_CAMERAS } from './omron.js';
import { TIS_CAMERAS } from './tis.js';
export const CAMERA_VENDORS = [
    {
        name: 'Basler',
        cameras: BASLER_CAMERAS,
        decode: decodeBasler,
        source: 'https://www.baslerweb.com/en/cameras/ace2/#products',
        caveat: 'Net list prices, published by Basler — not partner pricing, so a quote starts ' +
            'here rather than ends here. Sensor model and pixel size are not encoded in the ' +
            'part number and are left blank.',
    },
    {
        name: 'Omron',
        cameras: OMRON_CAMERAS,
        // No decoder: STC part numbers give megapixels but not width, and no frame
        // rate at all. Deriving 2448 px from '500' would be a fabricated number in
        // the field the field-of-view calculation depends on.
        source: 'https://industrial.omron.eu/en/products/area-scan',
        caveat: 'Quoted on request — Omron publishes no list prices, so these stay blank until ' +
            'a quote comes back. Blank here means unknown, never free, and never cheaper ' +
            'than a priced rival. Area scan only; the line scan and board-level ranges are ' +
            'not transcribed yet.',
    },
    {
        name: 'Lucid',
        cameras: LUCID_CAMERAS,
        // No decoder: Lucid part numbers round the megapixels (12.3 MP is coded 120)
        // and carry no frame rate, so the transcribed table is the only source.
        source: 'https://thinklucid.com/',
        caveat: 'Quoted through the channel — Lucid publishes no list prices, so these stay ' +
            'blank; blank means unknown, never free. Mono and colour are separate order ' +
            'numbers with identical specs. Area scan only; polarised and time-of-flight ' +
            'ranges are excluded, and the few models missing a published frame rate are ' +
            'left out rather than guessed.',
    },
    {
        name: 'The Imaging Source',
        cameras: TIS_CAMERAS,
        // No decoder: the part numbers give mono/colour but none of the figures, so
        // the transcribed finder table is the only source.
        source: 'https://www.theimagingsource.com/en-us/product/camerafinder/',
        caveat: 'Quoted on request — The Imaging Source publishes no list prices, so these stay ' +
            'blank; blank means unknown, never free. GigE and USB3 area-scan only: the USB 2.0, ' +
            'embedded-vision (MIPI/FPD-Link/GMSL2) and Polarsens ranges are excluded. Lens ' +
            'mount is not on the finder table, so it is left blank rather than assumed.',
    },
    {
        name: 'Allied Vision',
        cameras: ALLIED_CAMERAS,
        // No decoder: the selector JSON is the source, and part numbers are not
        // self-describing in the fields the selector needs.
        source: 'https://www.alliedvision.com/en/products/area-scan-cameras/',
        caveat: 'Quoted on request — Allied Vision publishes no list prices, so these stay blank; ' +
            'blank means unknown, never free. Only the fixed-part-number families (FXO, EXO, HR, ' +
            'EoSens, Goldeye, …); the build-to-configuration lines (Alvium, Mako, Manta, ' +
            'Prosilica) are excluded because they have no single order number per mono/colour ' +
            'variant. Line-scan, embedded-vision (MIPI/FPD-Link) and polarised ranges are out too.',
    },
];
/** Every camera from every known vendor. The default pool for the selector. */
export const ALL_CAMERAS = CAMERA_VENDORS.flatMap((v) => v.cameras);
export function findVendor(name) {
    return CAMERA_VENDORS.find((v) => v.name.toLowerCase() === name.toLowerCase());
}
/**
 * Look up a camera by vendor and part number.
 *
 * Tries the vendor's published list first and its decoder second. That order
 * matters: the list is what the vendor says exists, while a decoder will happily
 * parse a well-formed part number for a model that was never made. Falling back
 * to it means a newly released camera can still be adopted, but only after the
 * published list has been given the chance to answer.
 */
export function findCamera(vendorName, model) {
    const vendor = findVendor(vendorName);
    if (!vendor)
        return null;
    const listed = vendor.cameras.find((c) => c.model === model);
    if (listed)
        return listed;
    return vendor.decode?.(model) ?? null;
}
export const LENS_VENDORS = [
    {
        name: 'Computar',
        lenses: COMPUTAR_LENSES,
        source: 'https://www.computar.com/',
        caveat: 'Quoted on request — Computar publishes no list prices, so these stay blank; ' +
            'blank means unknown, never free. Figures are read off the listing spec line, ' +
            'not decoded from the order number. Aperture, image format and megapixels are ' +
            'left blank where a card omits them.',
    },
];
/** Every lens from every known vendor. The default pool for the lens selector. */
export const ALL_LENSES = LENS_VENDORS.flatMap((v) => v.lenses);
export function findLensVendor(name) {
    return LENS_VENDORS.find((v) => v.name.toLowerCase() === name.toLowerCase());
}
/**
 * Look up a lens by vendor and order number. Unlike cameras there is no decoder
 * fallback: Computar's numbering does not decode cleanly (see computar.ts), so a
 * lens exists to be adopted only if it is in the published list.
 */
export function findLens(vendorName, model) {
    const vendor = findLensVendor(vendorName);
    if (!vendor)
        return null;
    return vendor.lenses.find((l) => l.model === model) ?? null;
}
export * from './cameras.js';
export * from './lenses.js';
export * from './allied.js';
export * from './basler.js';
export * from './omron.js';
export * from './lucid.js';
export * from './tis.js';
export * from './computar.js';
//# sourceMappingURL=index.js.map